import {getProductList} from '@/services/ProductsService/product'
import {selectCountryCode} from '@/store/slices/countryCodeSlice'
import {selectActivePromotionTabIndex} from '@/store/slices/commonSlice'
import {useAppDispatch} from '@/store'
import {useQuery} from '@tanstack/react-query'
import {useMemo} from 'react'
import {useSelector} from 'react-redux'
import {BuyType} from '@/utils/constants/promotionConstants'

export const useProductListForModal = (searchArr = [], sortingObj = {}, pageIndex = 1, type = BuyType.PRODUCT_ID) => {
  
  const dispatch = useAppDispatch()
  const isCombo = type === BuyType.COMBO_ID
  const countryCode = useSelector(selectCountryCode)
  const activePromotionTabIndex = useSelector(selectActivePromotionTabIndex)
  // Create query parameters based on sorting and pagination
  const queryParams = useMemo(() => {
    const params = new URLSearchParams()

    // Use provided sort, or fallback to default
    if (sortingObj?.column) {
      params.append('sortBy', sortingObj.column)
      params.append('order', sortingObj.direction)
    } else {
      params.append('sortBy', 'productName')
      params.append('order', 'ASC')
    }

    // Pagination
    params.append('page', pageIndex)
    params.append('pageSize', '10')

    return params.toString()
  }, [sortingObj?.column, sortingObj?.direction, pageIndex])

  const productSearchArr = useMemo(() => [
    ...(searchArr || []),
    ...(countryCode === 'CO'
      ? [
          {
            field: 'salesOrg',
            value: activePromotionTabIndex == 0 ? 'CO03' : 'CO3P',
            type: 'string',
          },
        ]
      : []),
    {
      field: 'isCombo',
      value: 'false',
      type: 'string',
    },
  ], [searchArr, countryCode, activePromotionTabIndex])

  const comboSearchArr = useMemo(() => [
    ...(searchArr || []),
    {
      field: 'isCombo',
      value: 'true',
      type: 'string',
    },
    {
      field: 'comboStatus',
      value: 'PUBLISHED',
      type: 'string',
    },
    {
      field: 'status',
      value: 'Active',
      type: 'string',
    },
  ], [searchArr])


  const finalSearchArr = isCombo ? comboSearchArr : productSearchArr


  // Use TanStack Query to fetch the product list
  const {
    data: products,
    isLoading,
    isError,
    error,
    refetch } = useQuery({
      queryKey: ['PRODUCT_LIST_MODAL', queryParams, finalSearchArr],
      queryFn: () => getProductList(finalSearchArr, queryParams),
      staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
      cacheTime: 10 * 60 * 1000, // Cache for 10 minutes
    })

  // Process error into a user-friendly message
  const errorMessage = useMemo(() => {
    if (!isError) return null

    if (error?.message) {
      return `Failed to load products: ${error.message}`
    }

    return 'Failed to load products'
  }, [isError, error])

  return {
    products: products || {items: []},
    isLoading,
    isError,
    error: errorMessage,
    refetch,
  }
}
